/* eslint-disable @typescript-eslint/no-unsafe-call */
import { Type } from "class-transformer";
import {
	ArrayUnique,
	IsDateString,
	IsEnum,
	IsIn,
	IsOptional,
	Validate,
	ValidateIf,
	ValidateNested,
	ValidationArguments,
	ValidatorConstraint,
	ValidatorConstraintInterface,
} from "class-validator";

import {
	IsArray,
	IsBoolean,
	IsEmail,
	IsNumber,
	IsSanitizedHtml,
	isString,
	IsString,
} from "@wog/mol-lib-api-contract/utils";
import { acpV3PropsMaxLengthV2, HasHealthConcernValues, LEGAL_AGE } from "common/constant/v2/acp";
import { IsLocalPhoneNumber, IsNRIC, ValidResidentialAndPassType } from "common/validators";
import {
	CarePreferenceSelection,
	ConcernType,
	FearType,
	GoalsOfCare,
	HasExperienceValues,
	NHSRelationship,
	RELIGIONS,
	SourceOfSupport,
	ThingYouEnjoyInLifeSelection,
} from "../enums";

@ValidatorConstraint({ name: "validOtherRelationship", async: false })
export class ValidOtherRelationship implements ValidatorConstraintInterface {
	validate(value: any, args: ValidationArguments) {
		const object = args.object as SubmitDraftNhsDetailsDto;
		if (value && object.relationship !== NHSRelationship.OTHER) {
			return false;
		}

		// Require value to be present when OTHER was choosen
		if (!value && object.relationship === NHSRelationship.OTHER) {
			return false;
		}

		return true; // valid in all other cases
	}

	defaultMessage() {
		return `relationship value must be Other if otherRelationship has value or relationship value must not be Other if otherRelationship does not have value.`;
	}
}

@ValidatorConstraint({ name: "validCarePreferenceSelectionDetails" })
export class ValidCarePreferenceSelectionDetails implements ValidatorConstraintInterface {
	async validate(value: any, args: ValidationArguments) {
		const carePreferences = args.object as SubmitDraftCarePreferenceDto;

		// Only validate if user select CARE_OTHERS option
		if (!carePreferences.selections.includes(CarePreferenceSelection.CARE_OTHERS)) {
			return true;
		}

		// If CARE_OTHERS selected, selectionDetail should be available
		if (!carePreferences.selectionsDetail?.CARE_OTHERS?.CARE_OTHERS_DEC) {
			return false;
		}

		const validationResult = await isString(
			carePreferences.selectionsDetail.CARE_OTHERS.CARE_OTHERS_DEC,
			undefined,
			{ isOptional: false, maxLength: acpV3PropsMaxLengthV2.carePreference.otherCarePreference },
		);
		return validationResult.pass;
	}

	defaultMessage() {
		return "care preference must not be empty if CARE_OTHERS was selected";
	}
}

@ValidatorConstraint({ name: "validNhsDob" })
export class ValidNhsDob implements ValidatorConstraintInterface {
	async validate(value: any, args: ValidationArguments) {
		// dob should be a valid Date by now
		const dob = new Date(value);
		const now = new Date();

		let age = now.getFullYear() - dob.getFullYear();

		const monthDiff = now.getMonth() - dob.getMonth();
		if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < dob.getDate())) {
			age--;
		}

		return age >= LEGAL_AGE;
	}

	defaultMessage() {
		return `NHS must be at least ${LEGAL_AGE} years old`;
	}
}

export class SubmitDraftRegisteredAddress {
	@IsString({ maxLength: acpV3PropsMaxLengthV2.personalDetails.block })
	block!: string;

	@IsString({ maxLength: acpV3PropsMaxLengthV2.personalDetails.street })
	street!: string;

	@IsString({ maxLength: acpV3PropsMaxLengthV2.personalDetails.level, isOptional: true })
	floor!: string;

	@IsString({ maxLength: acpV3PropsMaxLengthV2.personalDetails.unit, isOptional: true })
	unit!: string;

	@IsString({ maxLength: acpV3PropsMaxLengthV2.personalDetails.postalCode })
	postal!: string;
}

export class SubmitDraftPersonalDetailsDtoV2 {
	@IsString({ maxLength: acpV3PropsMaxLengthV2.personalDetails.name })
	name!: string;

	@IsNRIC()
	@IsString({ maxLength: acpV3PropsMaxLengthV2.personalDetails.nric })
	uinfin!: string;

	@IsDateString()
	@IsString({ maxLength: acpV3PropsMaxLengthV2.personalDetails.dob })
	dob!: string;

	@IsString({ maxLength: acpV3PropsMaxLengthV2.personalDetails.gender })
	sex!: string;

	@IsString({ maxLength: acpV3PropsMaxLengthV2.personalDetails.phone })
	@IsLocalPhoneNumber()
	mobileNo!: string;

	@IsEmail({ maxLength: acpV3PropsMaxLengthV2.personalDetails.email })
	email!: string;

	@IsString({ maxLength: acpV3PropsMaxLengthV2.personalDetails.race, isOptional: true })
	race!: string;

	@IsString({ maxLength: acpV3PropsMaxLengthV2.personalDetails.maritalStatus, isOptional: true })
	marital!: string;

	@Validate(ValidResidentialAndPassType)
	@IsString({ maxLength: acpV3PropsMaxLengthV2.personalDetails.citizenship, isOptional: true })
	residentialStatus!: string;

	@IsString({ isOptional: true, maxLength: acpV3PropsMaxLengthV2.personalDetails.citizenship })
	passtype?: string;

	// TODO: Remove RegisteredAddress from src/app/modules/acp/hooks/useAcpForm/type.ts later
	@ValidateNested({ each: true })
	@Type(() => SubmitDraftRegisteredAddress)
	registeredAddress!: SubmitDraftRegisteredAddress;

	// TODO: Should we move this enum to ACP enum file?
	// TODO: ValidateIf + IsOptional does not affect @IsIn. Fix this later
	@IsOptional()
	@IsIn([...Object.keys(RELIGIONS), ""])
	religion?: string;
}

export class ConcernsDto {
	@IsEnum(ConcernType)
	concernType!: ConcernType;

	@IsString({ maxLength: acpV3PropsMaxLengthV2.healthConcerns.description })
	@IsSanitizedHtml({ sanitizeHtmlOptions: { allowedTags: [] } })
	description!: string;

	@IsBoolean()
	isSelected!: boolean;
}

export class SubmitDraftHealthConcernDto {
	@ValidateIf((healthConcerns) => healthConcerns.isConcerned !== "")
	@IsEnum(HasHealthConcernValues)
	isConcerned!: HasHealthConcernValues;

	@ValidateIf(
		(healthConcerns: SubmitDraftHealthConcernDto) => healthConcerns.isConcerned === HasHealthConcernValues.YES,
	)
	@ValidateNested({ each: true })
	@Type(() => ConcernsDto)
	concerns!: ConcernsDto[];
}

export class SubmitDraftPersonalExperienceDto {
	@ValidateIf((personalExperiences) => personalExperiences.hasExperiences !== "")
	@IsEnum(HasExperienceValues)
	hasExperiences!: HasExperienceValues;

	@IsString({ isOptional: true, maxLength: acpV3PropsMaxLengthV2.personalExperience.description })
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } })
	statement?: string;
}

export class OtherThingsYouEnjoyInLife {
	@IsString({ maxLength: acpV3PropsMaxLengthV2.thingsYouEnjoyInLife.otherSelection })
	OTHERS_ACTIVITY?: string;

	@IsString({ isOptional: true, maxLength: acpV3PropsMaxLengthV2.thingsYouEnjoyInLife.description })
	OTHERS_DESCRIPTION_IMPORTANCES?: string;
}

export class SubmitDraftThingsYouEnjoyInLifeDto {
	@IsArray({ minLength: 1 })
	@IsEnum(ThingYouEnjoyInLifeSelection, { each: true })
	selections!: ThingYouEnjoyInLifeSelection[];

	@Type(() => OtherThingsYouEnjoyInLife)
	@ValidateIf((thingsYouEnjoy: SubmitDraftThingsYouEnjoyInLifeDto) =>
		thingsYouEnjoy.selections.includes(ThingYouEnjoyInLifeSelection.OTHERS),
	)
	@ValidateNested()
	selectionsDetail!: OtherThingsYouEnjoyInLife;
}

export class SubmitDraftSourcesOfSupportDto {
	@IsEnum(SourceOfSupport, { each: true })
	sources!: SourceOfSupport[];

	@IsString({ isOptional: true, maxLength: acpV3PropsMaxLengthV2.sourceOfSupport.description })
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } })
	description?: string;

	@ValidateIf(
		(sourceOfSupport: SubmitDraftSourcesOfSupportDto) =>
			sourceOfSupport.sources?.length > 0 && sourceOfSupport.sources.includes(SourceOfSupport.OTHERS),
	)
	@IsString({ maxLength: acpV3PropsMaxLengthV2.sourceOfSupport.otherSource })
	@IsSanitizedHtml({ sanitizeHtmlOptions: { allowedTags: [] } })
	otherSources?: string;
}

export class FearsItemDto {
	@IsEnum(FearType)
	fearType!: FearType;

	@ValidateIf((fearItem: FearsItemDto) => fearItem.fearType === FearType.OTHERS)
	@IsString({ maxLength: acpV3PropsMaxLengthV2.fears.otherFear })
	@IsSanitizedHtml({ sanitizeHtmlOptions: { allowedTags: [] } })
	othersType?: string;

	@IsString({ isOptional: true, maxLength: acpV3PropsMaxLengthV2.fears.description })
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } })
	description?: string;

	@IsBoolean({ isOptional: true })
	isSelectedOther?: boolean;
}

export class SubmitDraftFearsDto {
	@IsArray({ minLength: 1 })
	@ValidateNested({ each: true })
	@Type(() => FearsItemDto)
	fears!: FearsItemDto[];
}

export class SubmitDraftGoalsOfCareDto {
	@IsEnum(GoalsOfCare)
	goalsOfCare!: GoalsOfCare;

	@IsString({ maxLength: acpV3PropsMaxLengthV2.goalsOfCare.description })
	@IsSanitizedHtml({ sanitizeHtmlOptions: { allowedTags: [] } })
	description!: string;

	@ValidateIf((goalOfCare: SubmitDraftGoalsOfCareDto) => goalOfCare.goalsOfCare === GoalsOfCare.LIFE_SUSTAINING)
	@IsString({ maxLength: acpV3PropsMaxLengthV2.goalsOfCare.procedureToAvoid })
	@IsSanitizedHtml({ sanitizeHtmlOptions: { allowedTags: [] } })
	outcomesToAvoid?: string;
}

export class CarePreferenceSelectionOthersDtoV2 {
	@IsString({ isOptional: true, maxLength: acpV3PropsMaxLengthV2.carePreference.otherCarePreference })
	CARE_OTHERS_DEC!: string;

	@IsString({ isOptional: true, maxLength: acpV3PropsMaxLengthV2.carePreference.description })
	CARE_OTHERS_ITEMS!: string;
}

export class CarePreferenceSelectionDetailDtoV2 implements Record<CarePreferenceSelection, unknown> {
	@IsString({ isOptional: true, maxLength: acpV3PropsMaxLengthV2.carePreference.description })
	PAIN_MANAGEMENT!: string;

	@IsString({ isOptional: true, maxLength: acpV3PropsMaxLengthV2.carePreference.description })
	CULTURAL_AND_SPIRITIAL_NEEDS!: string;

	@IsString({ isOptional: true, maxLength: acpV3PropsMaxLengthV2.carePreference.description })
	MOBILITY_AND_EXERCISE!: string;

	@IsString({ isOptional: true, maxLength: acpV3PropsMaxLengthV2.carePreference.description })
	COMPANIONSHIP_AND_EMOTIONAL_SUPPORT!: string;

	@IsString({ isOptional: true, maxLength: acpV3PropsMaxLengthV2.carePreference.description })
	PERSONAL_HYGIENE!: string;

	@IsString({ isOptional: true, maxLength: acpV3PropsMaxLengthV2.carePreference.description })
	PERSONAL_COMFORT_ITEMS!: string;

	@ValidateNested()
	@Type(() => CarePreferenceSelectionOthersDtoV2)
	CARE_OTHERS!: CarePreferenceSelectionOthersDtoV2;
}

export class SubmitDraftCarePreferenceDto {
	@IsArray({ minLength: 1 })
	@IsEnum(CarePreferenceSelection, { each: true })
	@IsSanitizedHtml({ sanitizeHtmlOptions: { allowedTags: [] } }, { each: true })
	selections!: string[];

	@ValidateNested()
	@Validate(ValidCarePreferenceSelectionDetails)
	@Type(() => CarePreferenceSelectionDetailDtoV2)
	selectionsDetail!: CarePreferenceSelectionDetailDtoV2;
}

export class SubmitDraftNhsDetailsDto {
	@IsString({ maxLength: acpV3PropsMaxLengthV2.nhs.name })
	name!: string;

	@IsString({ maxLength: acpV3PropsMaxLengthV2.nhs.dob })
	@IsDateString()
	@Validate(ValidNhsDob)
	dob!: string;

	@IsEmail({ maxLength: acpV3PropsMaxLengthV2.nhs.email })
	@IsSanitizedHtml({ sanitizeHtmlOptions: { allowedTags: [] } })
	email!: string;

	@IsString({ maxLength: acpV3PropsMaxLengthV2.nhs.mobile })
	@IsSanitizedHtml({ sanitizeHtmlOptions: { allowedTags: [] } })
	@IsLocalPhoneNumber()
	mobile!: string;

	@IsEnum(NHSRelationship)
	relationship!: NHSRelationship;

	@ValidateIf((nhs: SubmitDraftNhsDetailsDto) => nhs.relationship === NHSRelationship.OTHER)
	@IsString({ maxLength: acpV3PropsMaxLengthV2.nhs.otherRelationship })
	@Validate(ValidOtherRelationship)
	otherRelationship!: string;

	@IsNRIC()
	@IsString({ maxLength: acpV3PropsMaxLengthV2.nhs.nric })
	@IsSanitizedHtml({ sanitizeHtmlOptions: { allowedTags: [] } })
	nric!: string;
}

export class SubmitDraftNhsDto {
	@ValidateNested({ each: true, always: true })
	@Type(() => SubmitDraftNhsDetailsDto)
	@IsArray({ maxLength: acpV3PropsMaxLengthV2.appointedNhs })
	@ArrayUnique((person: SubmitDraftNhsDetailsDto) => person.nric)
	spokesPersons!: SubmitDraftNhsDetailsDto[];
}

export class SubmitDraftAdditionalNhsRequestDto {
	@IsString({ isOptional: true, maxLength: acpV3PropsMaxLengthV2.nhsAdditionalRequests.request })
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } })
	request?: string;
}

export class SubmitDraftContentDtoV2 {
	@IsNumber({ min: 0 })
	currentStep!: number;

	@IsNumber({ min: 0 })
	latestStep!: number;

	@IsBoolean()
	suitabilityCheck!: boolean;

	// Step 1
	@ValidateNested({ each: true })
	@Type(() => SubmitDraftPersonalDetailsDtoV2)
	personalDetails!: SubmitDraftPersonalDetailsDtoV2;

	// Step 2
	@ValidateNested({ each: true })
	@Type(() => SubmitDraftHealthConcernDto)
	healthConcerns!: SubmitDraftHealthConcernDto;

	// Step 3
	@ValidateNested({ each: true })
	@Type(() => SubmitDraftPersonalExperienceDto)
	personalExperiences!: SubmitDraftPersonalExperienceDto;

	// Step 4
	@ValidateNested({ each: true })
	@Type(() => SubmitDraftThingsYouEnjoyInLifeDto)
	thingsYouEnjoyInLife!: SubmitDraftThingsYouEnjoyInLifeDto;

	@ValidateNested({ each: true })
	@Type(() => SubmitDraftSourcesOfSupportDto)
	sourcesOfSupport!: SubmitDraftSourcesOfSupportDto;

	// Step 5
	@ValidateNested({ each: true })
	@Type(() => SubmitDraftFearsDto)
	fears!: SubmitDraftFearsDto;

	// Step 6
	@ValidateNested({ each: true })
	@Type(() => SubmitDraftGoalsOfCareDto)
	goalsOfCare!: SubmitDraftGoalsOfCareDto;

	// Step 7
	@ValidateNested({ each: true })
	@Type(() => SubmitDraftCarePreferenceDto)
	carePreferences!: SubmitDraftCarePreferenceDto;

	// Step 8
	@ValidateNested({ each: true })
	@Type(() => SubmitDraftNhsDto)
	nhs!: SubmitDraftNhsDto;

	// Step 9
	@ValidateNested({ each: true })
	@Type(() => SubmitDraftAdditionalNhsRequestDto)
	nhsAdditionalRequests!: SubmitDraftAdditionalNhsRequestDto;
}

export class AcpSubmitDraftDtoV2 {
	@ValidateNested({ each: true, always: true })
	@Type(() => SubmitDraftContentDtoV2)
	draftContent!: SubmitDraftContentDtoV2;
}
